import clsx from "clsx";
import type { Component, JSX } from "solid-js";

interface ButtonCircleIconProps {
  icon: Component<{ class?: string }>;
  onClick?: () => void;
  "aria-label"?: string;
  class?: string;
  disabled?: boolean;
  active?: boolean;
  size?: "sm" | "md" | "lg";
  variant?: "default" | "primary" | "ghost";
}

export const ButtonCircleIcon: Component<ButtonCircleIconProps> = (props) => {
  const size = () => props.size || "md";
  const variant = () => props.variant || "default";

  const sizeClasses = () => {
    switch (size()) {
      case "sm":
        return "w-8 h-8 p-1.5";
      case "md":
        return "w-10 h-10 p-2.5";
      case "lg":
        return "w-12 h-12 p-3";
      default:
        return "w-10 h-10 p-2.5";
    }
  };

  const iconSize = () => {
    switch (size()) {
      case "sm":
        return "w-4 h-4";
      case "md":
        return "w-5 h-5";
      case "lg":
        return "w-6 h-6";
      default:
        return "w-5 h-5";
    }
  };

  const variantClasses = () => {
    if (props.active) {
      // For switch groups, the active state should use transparent background to show the sliding primary background
      if (variant() === "ghost") {
        return "bg-transparent text-on-primary dark:text-dark-on-primary";
      }
      return "bg-primary dark:bg-dark-primary text-on-primary dark:text-dark-on-primary";
    }

    switch (variant()) {
      case "primary":
        return "bg-primary dark:bg-dark-primary text-on-primary dark:text-dark-on-primary hover:bg-primary/90 dark:hover:bg-dark-primary/90";
      case "ghost":
        return "bg-transparent text-on-surface-variant dark:text-dark-on-surface-variant hover:bg-surface-decoration/20 dark:hover:bg-dark-surface-decoration/20 hover:text-on-surface dark:hover:text-dark-on-surface";
      default:
        return "bg-surface-decoration/30 dark:bg-dark-surface-decoration/30 text-on-surface-variant dark:text-dark-on-surface-variant hover:bg-surface-decoration/50 dark:hover:bg-dark-surface-decoration/50 hover:text-on-surface dark:hover:text-dark-on-surface";
    }
  };

  return (
    <button
      type="button"
      class={clsx(
        "rounded-full transition-all duration-200 cursor-pointer",
        // Only add border for non-active ghost buttons or non-ghost variants
        props.active && variant() === "ghost" ? "border-0" : "border border-transparent",
        "focus:outline-none",
        // Disable focus ring for active switch group buttons to prevent white flash
        props.active && variant() === "ghost"
          ? "focus:ring-0"
          : "focus:ring-2 focus:ring-primary/50 dark:focus:ring-dark-primary/50",
        "disabled:opacity-50 disabled:cursor-not-allowed",
        sizeClasses(),
        variantClasses(),
        props.class,
      )}
      onClick={props.onClick}
      aria-label={props["aria-label"]}
      disabled={props.disabled}
    >
      <props.icon class={iconSize()} />
    </button>
  );
};
